import cs from 'classnames';
import React from 'react';
import { useThrottleCallback } from '@react-hook/throttle';
import { injectStoresV2 } from '../../stores/injectStoresV2';
import { useOnWindowScroll } from '../../hooks/useOnWindowScroll';
import { CategoriesStore, CategoryGroup } from '../../stores/CategoriesStore';
import { RoutingStore } from '../../stores/RoutingStore';
import { HeaderStore } from '../../stores/HeaderStore';
import { SeoLink } from '../SeoLink/SeoLink';
import { WithTranslations, withTranslations } from '../../../utils/withTranslations';
import { Search } from '../Search/Search';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useScrollToTop } from '../../contexts/ScrollToTopContext';
import { BILoggerStore } from '../../stores/BILoggerStore';
import { ConfigStore } from '../../stores/ConfigStore';
import { headerMenuPanelDataHooks } from './HeaderMenuPanel.dataHooks';
import { HeaderMenu } from './HeaderMenu/HeaderMenu';
import styles from './HeaderMenuPanel.scss';

interface HeaderMenuPanelProps extends WithTranslations {
  categoriesStore: CategoriesStore;
  routingStore: RoutingStore;
  headerStore: HeaderStore;
  biLoggerStore: BILoggerStore;
  configStore: ConfigStore;
  dataHook?: string;
}

const HeaderMenuPanelCmp: React.FC<HeaderMenuPanelProps> = ({
  categoriesStore,
  dataHook,
  routingStore,
  headerStore,
  biLoggerStore,
  configStore,
  t,
}) => {
  const { isSticky } = headerStore;
  const { activeCategorySlug, primaryCategorySlug, locationBuilders } = routingStore;
  const { categoryGroups, getCategoryBySlug } = categoriesStore;
  const { currentLanguage } = configStore.config;
  const isSortingEnabled = currentLanguage === 'en';
  const isNewUi = configStore.config.newUI;

  const moreCategoryGroup: CategoryGroup = {
    key: 'more',
    displayNameKey: isSortingEnabled
      ? 'categoriesMenu.genericCategories.collections'
      : 'categoriesMenu.genericCategories.more',
    categories: [getCategoryBySlug('new'), getCategoryBySlug('most-popular')].filter(Boolean),
  };
  const headerPanelRef = React.useRef<HTMLDivElement>();
  const menuItemsRef = React.useRef<HTMLElement[]>([]);
  menuItemsRef.current = [];
  const { scrollToTop } = useScrollToTop();

  const addItemRef = (element: HTMLElement) => {
    if (element && !menuItemsRef.current.includes(element)) {
      menuItemsRef.current.push(element);
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = event;
    const isArrowLeft = key === 'ArrowLeft';
    const isArrowRight = key === 'ArrowRight';
    if (!isArrowLeft && !isArrowRight) {
      return;
    }

    const activeMenuItemIndex = menuItemsRef.current.findIndex((menuItem) => menuItem === document.activeElement);

    if (activeMenuItemIndex === -1) {
      return;
    }

    const getNewActiveMenuIndex = () => {
      if (isArrowLeft) {
        return activeMenuItemIndex > 0 ? activeMenuItemIndex - 1 : menuItemsRef.current.length - 1;
      }

      return activeMenuItemIndex + 1 < menuItemsRef.current.length ? activeMenuItemIndex + 1 : 0;
    };

    const newActiveMenuItem = menuItemsRef.current[getNewActiveMenuIndex()];
    newActiveMenuItem.focus();
  };

  const handleMenuItemClick = () => {
    scrollToTop();
  };

  const onAllTemplatesClick = () => {
    handleMenuItemClick();
    biLoggerStore.logCategorySelect({ clickSource: 'header', category: 'all' });
  };

  const onBlankTemplatesClick = () => {
    handleMenuItemClick();
    biLoggerStore.logCategorySelect({ clickSource: 'header', category: 'blank' });
  };

  const recalcSticky = React.useCallback(() => {
    if (headerPanelRef.current) {
      const headerPanelPlace = parseInt(window.getComputedStyle(headerPanelRef.current).top, 10);
      const headerPanelPositionOnScreen = headerPanelRef.current.getBoundingClientRect().top;
      const isHeaderMoved = headerPanelPositionOnScreen <= headerPanelPlace;
      headerStore.setIsSticky(isHeaderMoved);
    }
  }, [headerStore]);
  const throttleTimeout = 200;
  useOnWindowScroll(useThrottleCallback(recalcSticky, 1000 / throttleTimeout));

  const isNarrowScreen = useMediaQuery('(max-width: 1279px)');

  const isAllTemplates = activeCategorySlug === primaryCategorySlug;
  const isBlankTemplates = activeCategorySlug === 'blank';
  const isSingleHeader = currentLanguage !== 'en';

  return (
    <nav
      className={cs(styles.menuHolder, {
        [styles.sticky]: isSticky,
        [styles.singleHeader]: isSingleHeader,
        [styles.menuHolderNewUi]: isNewUi,
      })}
      onKeyDown={handleMenuKeyDown}
      data-hook={dataHook}
      ref={headerPanelRef}
      aria-label={t('header.menu.navigationHint')}
    >
      <ul className={styles.menu} aria-label={t('categoriesMenu.categoriesBySubject')}>
        {categoryGroups.map((categoryGroup) => (
          <HeaderMenu
            dataHook={headerMenuPanelDataHooks.headerMenu({ key: categoryGroup.key })}
            categoryGroup={categoryGroup}
            key={categoryGroup.key}
            onItemClick={handleMenuItemClick}
            setToggleRef={addItemRef}
            adjustWidth
          />
        ))}
      </ul>
      <ul className={styles.rightMenu} aria-label={t('categoriesMenu.genericCategories')}>
        <li>
          <SeoLink
            className={cs(styles.menuLink, { [styles.active]: isAllTemplates, [styles.newMenuLink]: isNewUi })}
            dataHook={headerMenuPanelDataHooks.allTemplatesLink()}
            onClick={onAllTemplatesClick}
            to={locationBuilders.home({})}
            setRef={addItemRef}
            data-bi-element="category"
            data-bi-element-value="all"
          >
            {isNarrowScreen ? t('overrides.menu.smallScreen.all') : t('overrides.menu.all')}
          </SeoLink>
        </li>
        <li className={cs(styles.delimiter, { [styles.newDelimiter]: isNewUi })} aria-hidden="true" />
        {!isSticky && (
          <li>
            <SeoLink
              className={cs(styles.menuLink, { [styles.active]: isBlankTemplates, [styles.newMenuLink]: isNewUi })}
              dataHook={headerMenuPanelDataHooks.blankTemplatesLink()}
              onClick={onBlankTemplatesClick}
              to={locationBuilders.category({ categorySlug: 'blank' })}
              setRef={addItemRef}
              data-bi-element="category"
              data-bi-element-value="blank"
            >
              <span>{t('header.menu.blankTemplates')}</span>
            </SeoLink>
          </li>
        )}
        {!isSticky && <li className={cs(styles.delimiter, { [styles.newDelimiter]: isNewUi })} aria-hidden="true" />}
        {!isSticky && (
          <HeaderMenu
            dataHook={headerMenuPanelDataHooks.moreCategoriesMenu()}
            onItemClick={handleMenuItemClick}
            categoryGroup={moreCategoryGroup}
            setToggleRef={addItemRef}
            isRightAlignedDropdown
          />
        )}
      </ul>
      {isSticky && <Search dataHook={headerMenuPanelDataHooks.search()} />}
    </nav>
  );
};

export const HeaderMenuPanel = withTranslations(
  injectStoresV2('categoriesStore', 'headerStore', 'routingStore', 'biLoggerStore', 'configStore')(HeaderMenuPanelCmp),
);
